import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBoards, getCurrentRound, getMentionsUnread } from '../core/api';
import { SEASON_ID } from '../core/config';
import { useT } from '../core/lexicon';
import { useAsync } from '../core/useAsync';
import { countdown, ordinal } from '../core/util';
import { useApp } from '../App';
import { RailLogo } from '../core/RailLogo';
import { Screen } from './Layout';
import type { CurrentRound } from '../core/types';

type ItemState = 'owed' | 'done' | 'open' | 'locked';
interface Item {
  label: string;
  to: string;
  state: ItemState;
  tag?: string;
  badge?: number;
}

/**
 * The home menu (spec §20): every screen one tap away, each row carrying its own
 * TO DO / DONE state so "what do I owe" is answered without opening anything. The
 * cursor starts on the first outstanding task. Phase decides what is actionable —
 * Vote/Veto/Guess are locked until the round reaches VOTING.
 */
export function Menu() {
  const t = useT();
  const nav = useNavigate();
  const { user, style } = useApp();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const boards = useAsync(() => getBoards(SEASON_ID), []);

  // Unread @-mentions → a count badge on the Chat item. Poll gently while the
  // menu is open; opening Chat marks them seen and the next fetch reads 0.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () =>
      getMentionsUnread()
        .then((r) => alive && setUnread(r.count))
        .catch(() => {});
    load();
    const id = window.setInterval(load, 45000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  const placing = useMemo(() => {
    const pts = boards.data?.points;
    if (!pts) return null;
    const i = pts.findIndex((r) => r.user_id === user.user_id);
    return i >= 0 ? i + 1 : null;
  }, [boards.data, user.user_id]);

  const items = useMemo<Item[]>(
    () => buildItems(t, round.data ?? null, placing, user.is_admin, unread),
    [t, round.data, placing, user.is_admin, unread],
  );

  // Cursor starts on the first thing you owe, else the first thing you can open.
  const firstActionable = useMemo(() => {
    const owed = items.findIndex((i) => i.state === 'owed');
    if (owed >= 0) return owed;
    const open = items.findIndex((i) => i.state !== 'locked');
    return open >= 0 ? open : 0;
  }, [items]);

  const [cur, setCur] = useState(0);
  useEffect(() => setCur(firstActionable), [firstActionable]);

  const move = (delta: number) => {
    setCur((c) => (c + delta + items.length) % items.length);
  };
  const choose = (i: number) => {
    const it = items[i];
    if (it.state === 'locked') return;
    nav(it.to);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        move(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        move(-1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        choose(cur);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, items]);

  const r = round.data ?? null;
  const isVoting = r?.phase === 'VOTING';
  const deadline = r ? countdown(isVoting ? r.voting_deadline : r.submission_deadline) : null;
  const tally = r
    ? isVoting
      ? { l: 'tally.ballots', a: r.ballots_in, b: r.expected_players }
      : { l: 'tally.submissions', a: r.submissions_in, b: r.expected_players }
    : null;

  return (
    <Screen>
      <header className="top stack">
        {style === 'rail' ? <RailLogo /> : null}
        <div className="top-row">
          <span className="round">{r ? t('round.label', { n: r.sequence_no }) : round.loading ? '' : t('app.name')}</span>
          {deadline ? <span className="left">{deadline} LEFT</span> : null}
        </div>
        <div className="theme-line">
          {r ? r.theme.toUpperCase() : round.loading ? <span className="spin">…</span> : t('empty.no_round')}
        </div>
      </header>

      {r && tally ? (
        <section className="tally">
          <div className="tally-head">
            <span className="tally-l">{t(isVoting ? 'phase.points.name' : 'phase.submit.name').toUpperCase()}</span>
            <span className="tally-c">{t(tally.l, { a: tally.a, b: tally.b })}</span>
          </div>
          <div className="pips" role="img" aria-label={t(tally.l, { a: tally.a, b: tally.b })}>
            {Array.from({ length: tally.b }, (_, i) => (
              <div key={i} className={`pip${i < tally.a ? ' in' : ''}`} />
            ))}
          </div>
        </section>
      ) : null}

      {r ? (
        <button className="progress-link" onClick={() => nav('/progress')}>
          {t('progress.name').toUpperCase()} ({r.ballots_in}/{r.expected_players}) ›
        </button>
      ) : null}

      <nav className="menu">
        <div className="frame">
          {items.map((it, i) => (
            <button
              key={it.label + i}
              className={`item${i === cur ? ' on' : ''}${it.state === 'locked' ? ' off' : ''}`}
              disabled={it.state === 'locked'}
              onMouseEnter={() => setCur(i)}
              onClick={() => {
                setCur(i);
                choose(i);
              }}
            >
              <span className="cur">▶</span>
              <span>{it.label.toUpperCase()}</span>
              {it.badge ? (
                <span className="count" aria-label={`${it.badge} unread`}>{it.badge}</span>
              ) : it.tag ? (
                <span className={`tag ${it.state === 'owed' ? 'owed' : 'plain'}`}>{it.tag}</span>
              ) : (
                <span />
              )}
            </button>
          ))}
        </div>
      </nav>

      <footer className="foot">
        <span>{r || round.loading ? '' : t('empty.no_round')}</span>
        <span>{placing ? ordinal(placing).toUpperCase() : ''}</span>
      </footer>
    </Screen>
  );
}

function buildItems(
  t: ReturnType<typeof useT>,
  r: CurrentRound | null,
  placing: number | null,
  isAdmin: boolean,
  unread: number,
): Item[] {
  const you = r?.you ?? null;
  const phase = r?.phase;
  const owed = t('state.owed').toUpperCase();
  const done = t('state.done').toUpperCase();

  // Submit
  let submit: Item;
  if (you?.submitted) submit = { label: t('phase.submit.name'), to: '/submit', state: 'done', tag: done };
  else if (phase === 'SUBMITTING') submit = { label: t('phase.submit.name'), to: '/submit', state: 'owed', tag: owed };
  else submit = { label: t('phase.submit.name'), to: '/submit', state: 'locked' };

  const votingItem = (label: string, to: string, doneFlag: boolean): Item => {
    if (doneFlag) return { label, to, state: 'done', tag: done };
    if (phase === 'VOTING') return { label, to, state: 'owed', tag: owed };
    return { label, to, state: 'locked' };
  };

  const standingsTag = placing ? ordinal(placing).toUpperCase() : undefined;

  // Veto is a per-season admin toggle. Undefined = on (default); only false hides
  // it. When off there is no veto step anywhere and the ballot never needs one.
  const vetoOn = r?.veto_enabled !== false;
  const items: Item[] = [
    submit,
    votingItem(t('phase.points.name'), '/vote', !!you?.points_done),
    ...(vetoOn ? [votingItem(t('phase.veto.name'), '/veto', !!you?.veto_done)] : []),
    votingItem(t('phase.guess.name'), '/guess', !!you?.guesses_done),
    { label: t('chat.name'), to: '/chat', state: 'open', badge: unread || undefined },
    { label: t('nav.standings'), to: '/standings', state: 'open', tag: standingsTag },
    { label: t('phase.reveal.name'), to: '/results', state: 'open' },
    { label: t('schedule.name'), to: '/rounds', state: 'open' },
    { label: t('profile.name'), to: '/about', state: 'open' },
    { label: t('style.switch'), to: '/style', state: 'open' },
  ];
  if (isAdmin) items.push({ label: t('admin.role'), to: '/admin', state: 'open' });
  return items;
}
